import { useState, useEffect } from 'react';
import { message } from 'antd';
import { threadModelData, routeCommentModelData } from '../../../services/ModelService';

/**
 * Hook for managing support ticket threads and comments
 * Adapted from useUnifiedCommentSystem for support tickets
 */
export const useSupportTicketCommentSystem = (editor, companyId, operatorId) => {
  // State for threads
  const [availableThreads, setAvailableThreads] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [issueTitles, setIssueTitles] = useState({});
  
  // State for comments
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  
  // State for editing
  const [editingTitle, setEditingTitle] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  
  // State for resolution
  const [commentGroupResolved, setCommentGroupResolved] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedResolutionCode, setSelectedResolutionCode] = useState('');
  const [customResolutionCode, setCustomResolutionCode] = useState('');
  const [resolutionCodes, setResolutionCodes] = useState([
    'Fixed',
    'Won\'t Fix',
    'Duplicate',
    'Invalid',
    'Other'
  ]);
  
  // State for loading
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  
  // State for modal
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [selectedTicketForComment, setSelectedTicketForComment] = useState(null);
  
  // State for resolution comment
  const [resolutionComment, setResolutionComment] = useState(null);

  /**
   * Create a new support ticket thread
   */
  const createSupportTicketThread = async (threadData) => {
    try {
      const thread = await threadModelData.createThread(threadData);
      return thread;
    } catch (error) {
      console.error('Error creating support ticket thread:', error);
      throw error;
    }
  };

  /**
   * Load all support ticket threads for a company
   */
  const loadSupportTicketThreads = async () => {
    try {
      setIsLoading(true);
      const allThreads = await threadModelData.fetchAll();
      const supportTicketThreads = Object.values(allThreads).filter(thread => 
        thread.type === 'thread' &&
        thread.parentType === 'support_ticket' &&
        !thread.archived
      );
      
      // Load comment counts for each thread
      const threadsWithCommentCounts = await Promise.all(
        supportTicketThreads.map(async (thread) => {
          try {
            const allComments = await routeCommentModelData.getAll();
            const threadComments = Object.values(allComments).filter(comment => 
              comment.threadId === thread.threadId &&
              !comment.archived
            );
            return {
              ...thread,
              commentCount: threadComments.length
            };
          } catch (error) {
            console.error('Error loading comment count for thread:', thread.threadId, error);
            return {
              ...thread,
              commentCount: 0
            };
          }
        })
      );
      
      const titles = {};
      threadsWithCommentCounts.forEach(thread => {
        if (thread.title) {
          titles[thread.threadId] = thread.title;
        }
      });
      
      setIssueTitles(prev => ({ ...prev, ...titles }));
      setAvailableThreads(threadsWithCommentCounts.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
      setIsLoading(false);
      
      return threadsWithCommentCounts;
    } catch (error) {
      console.error('Error loading support ticket threads:', error);
      setIsLoading(false);
      return [];
    }
  };

  /**
   * Delete a support ticket thread (admin only)
   * Archives the thread instead of deleting it permanently
   */
  const handleDeleteThread = async (threadId) => {
    if (!threadId) return;
    try {
      await threadModelData.archiveThread(threadId);
      await loadSupportTicketThreads();
      message.success('Issue archived successfully');
    } catch (error) {
      console.error('Error archiving support ticket thread:', error);
      message.error('Failed to archive issue. Please try again.');
      throw error;
    }
  };

  /**
   * Load comments for a specific thread
   */
  const loadCommentsForThread = async (threadId) => {
    try {
      const allComments = await routeCommentModelData.getAll();
      const threadComments = Object.values(allComments).filter(comment => 
        comment.threadId === threadId &&
        !comment.archived
      );
      
      setComments(threadComments.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
    } catch (error) {
      console.error('Error loading comments for thread:', error);
      setComments([]);
    }
  };

  /**
   * Add a new comment to a thread
   */
  const handleAddComment = async (threadId, message) => {
    if (!message.trim() || !threadId) return;

    setIsSavingComment(true);
    try {
      const commentData = {
        message: message.trim(),
        author: typeof editor === 'string' ? editor : JSON.stringify(editor),
        lastEditor: typeof editor === 'string' ? editor : JSON.stringify(editor),
        readBy: [typeof editor === 'string' ? editor : JSON.stringify(editor)],
        type: 'comment',
        subType: 'bug',
        parentId: threadId, // Use threadId as parentId for OTR issues
        parentType: 'support_ticket',
        threadId: threadId,
        companyId: companyId,
        operatorId: operatorId,
        createdAt: Date.now()
      };

      const comment = await routeCommentModelData.save(commentData);
      
      // Reload comments for the thread
      await loadCommentsForThread(threadId);
      
      setNewComment('');
      
      return comment;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    } finally {
      setIsSavingComment(false);
    }
  };

  /**
   * Edit an existing comment
   */
  const handleEditComment = (comment) => {
    setEditingComment(comment.refId);
  };

  /**
   * Save edited comment
   */
  const handleSaveEditComment = async (commentRefId, newMessage) => {
    const refId = commentRefId || editingComment;
    const messageText = newMessage;
    
    if (!refId || !messageText.trim()) {
      setEditingComment(null);
      return;
    }

    try {
      const allComments = await routeCommentModelData.getAll();
      const commentToUpdate = Object.values(allComments).find(comment => 
        comment.refId === refId
      );

      if (!commentToUpdate) {
        return;
      }

      const updatedComment = {
        ...commentToUpdate,
        message: messageText.trim(),
        lastEditor: typeof editor === 'string' ? editor : JSON.stringify(editor),
        updatedAt: Date.now()
      };

      await routeCommentModelData.save(updatedComment);
      
      if (selectedTicketForComment) {
        await loadCommentsForThread(currentThreadId);
      }
      
      setEditingComment(null);
      
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  /**
   * Cancel comment editing
   */
  const handleCancelEditComment = () => {
    setEditingComment(null);
  };

  /**
   * Delete a comment
   */
  const handleDeleteComment = async (commentId) => {
    if (!commentId) return;

    setDeletingCommentId(commentId);
    try {
      const allComments = await routeCommentModelData.getAll();
      const commentToArchive = Object.values(allComments).find(comment => 
        comment.refId === commentId
      );

      if (!commentToArchive) {
        return;
      }

      const archivedComment = {
        ...commentToArchive,
        archived: true,
        lastEditor: typeof editor === 'string' ? editor : JSON.stringify(editor),
        updatedAt: Date.now()
      };

      await routeCommentModelData.save(archivedComment);
      
      if (selectedTicketForComment) {
        await loadCommentsForThread(currentThreadId);
      }
      
      
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setDeletingCommentId(null);
    }
  };

  /**
   * Handle comment modal close
   */
  const handleCommentModalClose = () => {
    setCommentModalVisible(false);
    setSelectedTicketForComment(null);
    setComments([]);
    setNewComment('');
    setCurrentThreadId(null);
    setAvailableThreads([]);
    setIssueTitles({});
    setEditingTitle(null);
    setEditingComment(null);
    setCommentGroupResolved(false);
    setResolutionComment(null);
  };

  /**
   * Handle opening comment modal for a ticket
   */
  const handleCommentClick = (ticket) => {
    setSelectedTicketForComment(ticket);
    setCommentModalVisible(true);
    
    // Find the thread for this ticket
    const thread = availableThreads.find(t => t.parentId === ticket.bugId);
    if (thread) {
      setCurrentThreadId(thread.threadId);
      loadCommentsForThread(thread.threadId);
    }
  };

  /**
   * Handle creating a new support ticket thread
   */
  const handleCreateSupportTicketThread = async (ticketData) => {
    try {
      const newThreadId = `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const threadData = {
        threadId: newThreadId,
        parentId: `ticket_${Date.now()}`,
        parentType: 'support_ticket',
        subType: 'bug',
        title: ticketData.title || 'New Support Ticket',
        status: 'open',
        author: typeof editor === 'string' ? editor : JSON.stringify(editor),
        lastEditor: typeof editor === 'string' ? editor : JSON.stringify(editor),
        companyId: companyId,
        operatorId: operatorId,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        metaInfo: {
          jiraId: ticketData.jiraId || null,
          breadcrumbs: Array.isArray(ticketData.breadcrumbs) ? ticketData.breadcrumbs.map(b => 
            typeof b === 'string' ? b : JSON.stringify(b)
          ) : (ticketData.breadcrumbs ? [ticketData.breadcrumbs] : []),
          url: ticketData.url || null,
          recordingUrl: ticketData.recordingUrl || null,
          logFileUrl: ticketData.logFileUrl || null
        }
      };

      const thread = await createSupportTicketThread(threadData);
      
      // Update local state
      setIssueTitles(prev => ({
        ...prev,
        [thread.threadId]: thread.title
      }));
      
      await loadSupportTicketThreads();
      
      return thread;
    } catch (error) {
      console.error('Error creating support ticket thread:', error);
      throw error;
    }
  };

  /**
   * Handle updating a support ticket thread (e.g., with JIRA ID)
   */
  const handleUpdateSupportTicketThread = async (threadId, updates) => {
    try {
      const allThreads = await threadModelData.fetchAll();
      const threadToUpdate = Object.values(allThreads).find(thread => 
        thread.threadId === threadId &&
        thread.type === 'thread' &&
        thread.parentType === 'support_ticket' &&
        !thread.archived
      );

      if (!threadToUpdate) {
        return;
      }

      const updatedThread = {
        ...threadToUpdate,
        ...updates,
        lastEditor: typeof editor === 'string' ? editor : JSON.stringify(editor),
        updatedAt: Date.now()
      };

      await threadModelData.save(updatedThread);
      
      // Update local state
      if (updates.title) {
        setIssueTitles(prev => ({
          ...prev,
          [threadId]: updates.title
        }));
      }
      
      await loadSupportTicketThreads();
      
      return updatedThread;
    } catch (error) {
      console.error('Error updating support ticket thread:', error);
      throw error;
    }
  };



  /**
   * Handle selecting a thread in the modal
   */
  const handleSelectThread = (threadId) => {
    setCurrentThreadId(threadId);
    if (selectedTicketForComment) {
      loadCommentsForThread(threadId);
    }
  };

  /**
   * Handle editing a thread title
   */
  const handleTitleEdit = async (threadId, newTitle) => {
    if (!newTitle || !newTitle.trim()) return;
    
    const trimmedTitle = newTitle.trim();
    
    try {
      if (threadId) {
        // Update existing thread
        await handleUpdateSupportTicketThread(threadId, { title: trimmedTitle });
      } else {
        // Create new thread
        const threadData = {
          title: trimmedTitle,
          parentId: selectedTicketForComment?.ticketId || `ticket_${Date.now()}`,
          parentType: 'support_ticket',
          subType: 'bug',
          author: typeof editor === 'string' ? editor : JSON.stringify(editor),
          lastEditor: typeof editor === 'string' ? editor : JSON.stringify(editor),
          companyId: companyId,
          operatorId: operatorId,
          metaInfo: {
            jiraId: selectedTicketForComment?.jiraId || null,
            breadcrumbs: selectedTicketForComment?.breadcrumbs || null,
            url: selectedTicketForComment?.url || null,
            recordingUrl: selectedTicketForComment?.recordingUrl || null,
            logFileUrl: selectedTicketForComment?.logFileUrl || null
          }
        };
        
        const thread = await createSupportTicketThread(threadData);
        setCurrentThreadId(thread.threadId);
      }
      
      setEditingTitle(null);
    } catch (error) {
      console.error('Error editing thread title:', error);
    }
  };

  /**
   * Handle key press for title editing
   */
  const handleTitleKeyPress = (e, threadId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleEdit(threadId, e.target.value);
    }
  };

  /**
   * Handle marking a thread as resolved
   */
  const handleMarkAsResolved = () => {
    setShowResolveModal(true);
  };

  /**
   * Handle reopening a resolved thread
   */
  const handleReopenGroup = async () => {
    if (!currentThreadId || !commentGroupResolved || isReopening) return;

    setIsReopening(true);
    try {
      await handleUpdateSupportTicketThread(currentThreadId, {
        status: 'open',
        resolutionCode: null,
        resolvedAt: null,
        resolvedBy: null
      });
      
    } catch (error) {
      console.error('Error reopening support ticket thread:', error);
    } finally {
      setIsReopening(false);
    }
  };

  /**
   * Handle resolving a thread with a code
   */
  const handleResolveWithCode = async () => {
    if (!currentThreadId || isResolving) return;

    const resolutionCode = selectedResolutionCode === 'Other' ? customResolutionCode : selectedResolutionCode;
    if (!resolutionCode.trim()) return;

    setIsResolving(true);
    try {
      await handleUpdateSupportTicketThread(currentThreadId, {
        status: 'resolved',
        resolutionCode: resolutionCode.trim(),
        resolvedAt: Date.now(),
        resolvedBy: typeof editor === 'string' ? editor : JSON.stringify(editor)
      });

      if (selectedResolutionCode === 'Other' && customResolutionCode.trim() && !resolutionCodes.includes(customResolutionCode.trim())) {
        setResolutionCodes(prev => [...prev, customResolutionCode.trim()]);
      }

      setShowResolveModal(false);
    } catch (error) {
      console.error('Error resolving support ticket thread:', error);
    } finally {
      setIsResolving(false);
    }
  };

  /**
   * Handle adding a custom resolution code
   */
  const handleAddResolutionCode = () => {
    if (customResolutionCode.trim() && !resolutionCodes.includes(customResolutionCode.trim())) {
      setResolutionCodes(prev => [...prev, customResolutionCode.trim()]);
      setSelectedResolutionCode(customResolutionCode.trim());
      setCustomResolutionCode('');
    }
  };

  // Update comment group resolved status when current thread changes
  useEffect(() => {
    if (currentThreadId && availableThreads.length > 0) {
      const currentThread = availableThreads.find(thread => thread.threadId === currentThreadId);
      if (currentThread) {
        setCommentGroupResolved(currentThread.status === 'resolved');
      }
    } else {
      setCommentGroupResolved(false);
    }
  }, [currentThreadId, availableThreads]);

  return {
    // Thread state
    availableThreads,
    currentThreadId,
    issueTitles,
    
    // Comment state
    comments,
    newComment,
    setNewComment,
    
    // Editing state
    editingTitle,
    setEditingTitle,
    editingComment,
    setEditingComment,
    
    // Resolution state
    commentGroupResolved,
    showResolveModal,
    setShowResolveModal,
    selectedResolutionCode,
    setSelectedResolutionCode,
    customResolutionCode,
    setCustomResolutionCode,
    resolutionCodes,
    setResolutionCodes,
    resolutionComment,
    setResolutionComment,
    
    // Loading state
    isLoading,
    isSavingComment,
    setIsSavingComment,
    isResolving,
    setIsResolving,
    isReopening,
    setIsReopening,
    deletingCommentId,
    setDeletingCommentId,
    
    // Modal state
    commentModalVisible,
    setCommentModalVisible,
    selectedTicketForComment,
    setSelectedTicketForComment,
    
    // Functions
    createSupportTicketThread,
    loadSupportTicketThreads,
    loadCommentsForThread,
    handleCreateSupportTicketThread,
    handleUpdateSupportTicketThread,
    handleCommentClick,
    handleCommentModalClose,
    handleSelectThread,
    handleTitleEdit,
    handleTitleKeyPress,
    handleMarkAsResolved,
    handleReopenGroup,
    handleResolveWithCode,
    handleAddResolutionCode,
    
    // Comment management functions
    handleAddComment,
    handleEditComment,
    handleSaveEditComment,
    handleCancelEditComment,
    handleDeleteComment,

    // Thread management functions
    handleDeleteThread
  };
};
